// useMatches.js
import { useState, useCallback } from "react";
import { getMatches } from "@/services";

const useMatches = () => {
  const [matches, setMatches] = useState([]);
  const [totalMatches, setTotalMatches] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [isMatchesLoading, setIsMatchesLoading] = useState(false);

  const [mlsMatches, setMlsMatches] = useState([]);
  const [totalMlsMatches, setTotalMlsMatches] = useState(0);
  const [hasNextMlsPage, setHasNextMlsPage] = useState(false);
  const [isMlsMatchesLoading, setIsMlsMatchesLoading] = useState(false);

  const fetchMatches = useCallback(async (query = {}, { onSuccess } = {}) => {
    const { wantAdId, inventoryId, mlsListingKey, page = 1 } = query;
    if (!wantAdId && !inventoryId && !mlsListingKey) return;

    setIsMatchesLoading(true);

    try {
      let invData = null;
      let mlsData = null;

      if (wantAdId) {
        // Want-Listing matches return BOTH surfaces. Inventory uses the
        // standard threshold (60) but MLS-feed listings score lower because
        // their N2H data is sparser than user-authored Inventory — we use
        // 20 for MLS so real matches surface. Two parallel requests so each
        // surface gets its own threshold cleanly. TODO: unify once MLS
        // scoring is refined (Phase 7 follow-up).
        const [invResp, mlsResp] = await Promise.all([
          getMatches({ ...query, minScore: 60 }),
          getMatches({ ...query, minScore: 20 }),
        ]);
        invData = invResp?.data || {};
        mlsData = mlsResp?.data?.mlsMatches || null;
      } else {
        // Inventory reverse-lookup (inventoryId) keeps the standard 60.
        const queryWithScore = { minScore: 60, ...query };
        const { data } = await getMatches(queryWithScore);
        invData = data || {};
        mlsData = data?.mlsMatches || null;
      }

      const invPayload = invData?.data || [];
      setMatches((prev) =>
        page > 1 ? [...prev, ...invPayload] : invPayload,
      );
      setTotalMatches(invData?.totalCount || 0);
      setHasNextPage(!!invData?.hasNextPage);

      if (mlsData) {
        const mlsPayload = mlsData.data || [];
        setMlsMatches((prev) =>
          page > 1 ? [...prev, ...mlsPayload] : mlsPayload,
        );
        setTotalMlsMatches(mlsData.totalCount || 0);
        setHasNextMlsPage(!!mlsData.hasNextPage);
      } else if (page === 1) {
        setMlsMatches([]);
        setTotalMlsMatches(0);
        setHasNextMlsPage(false);
      }

      onSuccess?.();
    } catch {
      if (page === 1) setMatches([]);
      setHasNextPage(false);
    } finally {
      setIsMatchesLoading(false);
    }
  }, []);

  const fetchMlsMatches = useCallback(async (query = {}, { onSuccess } = {}) => {
    const { mlsListingKey, page = 1 } = query;
    if (!mlsListingKey) return;

    setIsMlsMatchesLoading(true);
    // MLS reverse-lookup uses the same lower threshold as the MLS section
    // of the dual-surface fetchMatches path — see note there.
    const queryWithScore = { minScore: 20, ...query };

    try {
      const { data } = await getMatches(queryWithScore);
      const {
        data: payload = [],
        totalCount = 0,
        hasNextPage: nextPage = false,
      } = data || {};

      setMlsMatches((prev) => (page > 1 ? [...prev, ...payload] : payload));
      setTotalMlsMatches(totalCount);
      setHasNextMlsPage(nextPage);
      onSuccess?.();
    } catch {
      if (page === 1) setMlsMatches([]);
      setHasNextMlsPage(false);
    } finally {
      setIsMlsMatchesLoading(false);
    }
  }, []);

  return {
    matches,
    totalMatches,
    hasNextPage,
    isMatchesLoading,
    fetchMatches,
    mlsMatches,
    totalMlsMatches,
    hasNextMlsPage,
    isMlsMatchesLoading,
    fetchMlsMatches,
  };
};

export default useMatches;
